import { isEmpty, isNil } from 'lodash';
import sortBy from 'lodash/sortBy';

import { LabwareDefByDefURI } from '../../../labware/types';
import type { InstrumentForLocation, LabwareProperties } from '../../../types';
import * as LabwareErrors from '../../shared/labwareErrors';
import type {
  JsonLabwareById,
  MaybeWorkflowSerializationErrors,
  WorkflowSerializationError,
} from '../../shared/types';
import { getLocation, parseSlotString } from '../../shared/utils';
import { NULL_LABWARE_TYPE } from '../constants';
import { err, ok } from '../utils';

export default function labwareToJson(
  allDefinitions: LabwareDefByDefURI,
  labware: Record<string, LabwareProperties>,
  instruments: Record<string, InstrumentForLocation>
): MaybeWorkflowSerializationErrors<JsonLabwareById> {
  const errors: WorkflowSerializationError[] = [];
  const validLabware: JsonLabwareById = {};

  for (const l of sortBy(Object.values(labware), 'id')) {
    const { id, labwareDefinitionURI, maxIdleTime, nickname, startLocation } =
      l;
    let slots = l.slot === undefined ? undefined : parseSlotString(l.slot);

    // for single-slot instruments we automatically assign slot 1
    // as we don't expose an input for users to set it
    if (
      (isNil(slots) || isEmpty(slots)) &&
      !isNil(startLocation) &&
      instruments[startLocation]?.capacity === 1
    ) {
      slots = [1];
    }

    if (slots === undefined || slots === null) {
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.MISSING_START_LOCATION,
        labwareId: id,
      });

      continue;
    }

    if (labwareDefinitionURI === null || startLocation === null) {
      if (labwareDefinitionURI === null) {
        errors.push({
          errorType: 'labware',
          message: LabwareErrors.MISSING_LABWARE_DEFINITION_URI,
          labwareId: id,
        });
      }
      if (startLocation === null) {
        errors.push({
          errorType: 'labware',
          message: LabwareErrors.MISSING_START_LOCATION,
          labwareId: id,
        });
      }
      continue;
    }

    validLabware[id] = {
      id,
      description: nickname,
      type: labwareDefinitionURI,
      location: getLocation(startLocation, slots),
      max_idle_time: maxIdleTime,
      total_units:
        l.singleOrMultiUse === 'multi' && l.totalUnits !== null
          ? l.totalUnits
          : undefined,
    };

    const definition = allDefinitions[labwareDefinitionURI];

    if (definition === undefined) {
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.INVALID_LABWARE_DEFINITION_URI,
        labwareId: id,
        labwareDefinitionURI,
      });
    }
  }

  if (errors.length > 0) {
    return err(errors);
  }

  return ok(validLabware);
}

// TODO(AL, 10/07/2024): Loads of duplication here, consider refactoring
export function labwareToJsonForSave(
  labware: Record<string, LabwareProperties>,
  instruments: Record<string, InstrumentForLocation>
): JsonLabwareById {
  const validLabware: JsonLabwareById = {};

  const sortedLabware = sortBy(Object.values(labware), 'id');

  for (const {
    id,
    labwareDefinitionURI,
    maxIdleTime,
    nickname,
    startLocation,
    slot: rawSlot,
    totalUnits,
  } of sortedLabware) {
    let slot = rawSlot === undefined ? null : parseSlotString(rawSlot);

    // for single-slot instruments we automatically assign slot 1
    // as we don't expose an input for users to set it
    if (
      (isNil(slot) || isEmpty(slot)) &&
      !isNil(startLocation) &&
      instruments[startLocation]?.capacity === 1
    ) {
      slot = [1];
    }

    validLabware[id] = {
      id,
      description: nickname,
      type: labwareDefinitionURI || NULL_LABWARE_TYPE,
      location: slot === null ? [] : getLocation(startLocation ?? '', slot),
      max_idle_time: maxIdleTime,
      total_units: totalUnits ?? undefined,
    };
  }

  return validLabware;
}
